import React, { useRef } from 'react';
import { Alert, Button, Typography } from '@one-thd/sui-atomic-components';
import PropTypes from 'prop-types';
import constants from '../constants/constants';
import BannerConstants from '../constants/bannerConstants';
import { resendText } from '../common/sharedFunctions';
import { formatIntake } from '../utils/general';

export const successBanner = 'successBanner';

function BannerProvider({ children }) {
  const [inputAttribute, setInputAttribute] = React.useState('');
  const [successAlertText, setSuccessAlertText] = React.useState('');
  const [showSuccessAlert, setShowSuccessAlert] = React.useState(false);
  const [showErrorAlert, setShowErrorAlert] = React.useState(false);
  const [errorText, setErrorText] = React.useState(constants.GENERIC_ERROR);
  const [showIsPending, setShowIsPending] = React.useState(false);
  const [pendingText, setPendingText] = React.useState('');
  const [phoneNumber, setPhoneNumber] = React.useState('');
  const [phoneTableData, setPhoneTableData] = React.useState([]);
  const [showOptOutOfAllBanner, setShowOptOutOfAllBanner] = React.useState(false);
  const scrollToAlert = useRef(null);
  const offsetHeader = 150;

  React.useEffect(() => {
    if (scrollToAlert.current && (showSuccessAlert || showOptOutOfAllBanner)) {
      const { top } = scrollToAlert.current.getBoundingClientRect();
      window.scrollTo({
        top: window.scrollY + top - offsetHeader,
        behavior: 'smooth'
      });
    }
  }, [showSuccessAlert, showOptOutOfAllBanner]);

  function openSuccessAlert(message, intake) {
    if (intake !== null) {
      setInputAttribute(formatIntake(intake));
    }
    setSuccessAlertText(message);
    setShowSuccessAlert(true);
    setShowErrorAlert(false);
  }

  const closeSuccessAlert = () => {
    setShowSuccessAlert(false);
  };

  const closePendingAlert = () => {
    setShowIsPending(false);
  };

  function openPendingAlert(text, phonenumber, phoneTable) {
    setPendingText(text || constants.UPDATE_SUCCESS);
    setShowIsPending(true);
    setPhoneNumber(phonenumber);
    setPhoneTableData(phoneTable);
  }

  function openErrorAlert(text) {
    setErrorText(text || constants.GENERIC_ERROR);
    setShowErrorAlert(true);
    setShowSuccessAlert(false);
  }

  function closeErrorAlert() {
    setShowErrorAlert(false);
  }

  function hideBanners() {
    closeSuccessAlert();
    closeErrorAlert();
    closePendingAlert();
    setShowOptOutOfAllBanner(false);
  }

  function handlePendingResend() {
    resendText(phoneNumber, phoneTableData, () => {
      setShowIsPending(false);
      hideBanners();
      openSuccessAlert(BannerConstants.TEST_MESSAGE_OPT_IN);
    }, (err) => {
      setShowErrorAlert(true);
    });
  }

  function openOptOutOfAllBanner() {
    setShowOptOutOfAllBanner(true);
  }
  const closeOptOutOfAllBanner = () => {
    setShowOptOutOfAllBanner(false);
  };

  function optOutOfAllBanner() {
    return (showOptOutOfAllBanner && (
      <Alert ref={scrollToAlert} onClose={closeOptOutOfAllBanner} status="info">
        {constants.FCC_OPTOUT_INFORMATION}
      </Alert>
    ));
  }

  function successAlert() {
    const messages = Array.isArray(successAlertText) ? successAlertText : [successAlertText];

    return (showSuccessAlert && messages.map((message, index) => (
      <Alert
        key={index}
        aria-label={successBanner}
        ref={scrollToAlert}
        status="success"
        size="large"
        onClose={() => {
          const updatedMessages = messages.filter((item, i) => i !== index);
          if (updatedMessages.length === 0) {
            setShowSuccessAlert(false);
          } else {
            setSuccessAlertText(updatedMessages.length === 1 ? updatedMessages[0] : updatedMessages);
          }
        }}
      >
        <Typography variant="body-base">
          <Typography component="b" weight="bold">
            {inputAttribute}
          </Typography>
          {message}
        </Typography>
      </Alert>
    )));
  }

  function errorAlert() {
    return (showErrorAlert && (
      <Alert status="error">
        {errorText}
      </Alert>
    ));
  }

  function resendButton() {
    return (
      <Button
        style={{ fontSize: 14, textDecoration: 'underline' }}
        variant="text"
        className="rounded-full"
        onClick={() => handlePendingResend()}
      >
        Resend
      </Button>
    );
  }

  function pendingSmsAlert() {
    return (showIsPending && (
      <div className="sui-grid sui-flex-col sui-w-full">
        <Alert onClose={closePendingAlert} status="warning">
          <div className="sui-flex sui-gap-1" data-testid="pendingBanner">
            <Typography>
              {pendingText}
            </Typography>
            <div className="sui-px-5 sui-py-20 xl:sui-py-2 lg:sui-py-3 md:sui-py-4 sm:sui-py-10 xs:sui-py-20">
              <Typography className="sui-flex sui-flex-center" fontSize="16px">
                {resendButton()}
              </Typography>
            </div>

          </div>
        </Alert>
      </div>
    ));
  }

  return (
    <BannerContext.Provider
      value={{
        openSuccessAlert,
        closeSuccessAlert,
        openErrorAlert,
        closeErrorAlert,
        hideBanners,
        openPendingAlert,
        closePendingAlert,
        openOptOutOfAllBanner,
        closeOptOutOfAllBanner,
      }}
    >
      <div>
        {successAlert()}
        {errorAlert()}
        {pendingSmsAlert()}
        {optOutOfAllBanner()}
        {children}
      </div>
    </BannerContext.Provider>
  );
}

const BannerContext = React.createContext(null);

export { BannerContext, BannerProvider };

BannerProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
};
